import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { AppDispatch } from "configureStore";
import { useAppSelector } from "hooks";
import { setModalxComponent, setModalxComponentProps, setModalxStyle } from "components/modalx/modalx.slice";
import { fetchPersonnelFuelReports, PersonnelFuelReport } from "services/personnel.service";
import { canOpenFuelReport } from "./personnel-helpers";
import { panelClass, TablePagination } from "./PersonnelCardParts";

const cardClass = "rounded-lg border border-gray-200 bg-white shadow-sm";
const chipClass = "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium";
const thClass = "px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500";
const tdClass = "px-3 py-2 align-top text-sm text-gray-700";
// Dropped on a phone, where the table does not fit.
const hiddenOnPhone = "hidden md:table-cell";

/**
 * 'draft' | 'pending' | 'signed' | 'annulled'; anything else is shown as
 * sent. Exported: manual expenses share this same workflow/status set
 * (PersonnelExpensesTab reuses this chip rather than showing the raw code).
 */
export function FuelStatusChip({ status }: { status: string | null }) {
  const { t } = useTranslation();
  if (!status) return <span className="text-gray-400">—</span>;
  const tone =
    status === "signed"
      ? "bg-green-50 text-green-800"
      : status === "annulled"
        ? "bg-gray-100 text-gray-600"
        : status === "draft"
          ? "bg-gray-50 text-gray-600"
          : "bg-amber-50 text-amber-800";
  return (
    <span className={clsx(chipClass, tone)}>{t(`personnel_fuel_status_${status}`, { defaultValue: status })}</span>
  );
}

const number = (value: number | null, digits = 0): string => (value == null ? "—" : value.toLocaleString("lt-LT", { maximumFractionDigits: digits }));

/**
 * «Kuro ataskaitos»: this person's fuel reports, read only, paginated. The
 * period links into the fuel-reports module's own record only for a viewer
 * who holds that module's permission (Master / 'Access fuel reports') — a
 * head sees the tab but not the link, same as they never get a row's
 * `fileId`. The report's own document (when generated and the viewer may
 * see it) opens through the usual PdfView modal.
 */
export default function PersonnelFuelReportsTab({ companyId, userId }: { companyId: string; userId: string }) {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const role = useAppSelector((state) => state.authReducer.role);
  const canOpen = canOpenFuelReport(role);
  const [page, setPage] = useState(1);
  const [data, setData] = useState<{ rows: PersonnelFuelReport[]; total: number; limit: number } | null>(null);
  const [failed, setFailed] = useState(false);
  const requestRef = useRef(0);

  useEffect(() => {
    setPage(1);
  }, [companyId, userId]);

  const load = useCallback(() => {
    const requestId = ++requestRef.current;
    setFailed(false);
    fetchPersonnelFuelReports(companyId, userId, page)
      .then((result) => {
        if (requestId === requestRef.current) setData(result);
      })
      .catch(() => {
        if (requestId === requestRef.current) setFailed(true);
      });
  }, [companyId, userId, page]);

  useEffect(() => {
    setData(null);
    load();
  }, [load]);

  const openDocument = (fileId: string) => {
    dispatch(setModalxComponent("PdfView"));
    dispatch(setModalxStyle("large"));
    dispatch(setModalxComponentProps({ id: fileId, companyId }));
  };

  const statusAndDocument = (report: PersonnelFuelReport) => (
    <div className="flex flex-wrap items-center gap-1.5">
      <FuelStatusChip status={report.status} />
      {report.fileId && (
        <button
          type="button"
          className="text-xs font-medium text-blue-700 hover:underline max-md:py-2 max-md:text-sm"
          onClick={() => openDocument(report.fileId!)}
        >
          {t("view")}
        </button>
      )}
    </div>
  );

  if (failed) {
    return (
      <div className={clsx(panelClass, "flex items-center gap-3 text-sm text-red-700")}>
        {t("personnel_fuel_load_failed")}
        <button type="button" className="underline" onClick={load}>
          {t("retry")}
        </button>
      </div>
    );
  }
  if (!data) return <p className="text-sm text-gray-500">{t("loading")}</p>;

  return (
    <section className={cardClass}>
      {data.rows.length === 0 ? (
        <p className="p-4 text-sm text-gray-500">{t("personnel_fuel_none")}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100">
            <thead>
              <tr>
                <th className={thClass}>{t("personnel_fuel_period")}</th>
                <th className={thClass}>{t("personnel_fuel_asset")}</th>
                <th className={clsx(thClass, hiddenOnPhone)}>{t("personnel_fuel_km")}</th>
                <th className={clsx(thClass, hiddenOnPhone)}>{t("personnel_fuel_consumed_litres")}</th>
                <th className={thClass}>{t("personnel_fuel_amount")}</th>
                <th className={clsx(thClass, hiddenOnPhone)}>{t("status")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.rows.map((report) => (
                <tr key={report.id}>
                  <td className={tdClass}>
                    {canOpen ? (
                      <Link
                        to={`/app/${companyId}/fuel-reports/${report.id}`}
                        className="font-medium text-blue-700 hover:underline max-md:inline-block max-md:py-1.5"
                      >
                        {report.period || "—"}
                      </Link>
                    ) : (
                      <span className="font-medium text-gray-900">{report.period || "—"}</span>
                    )}
                    {/* Phone: the status column is hidden, so the status sits under the period. */}
                    <div className="mt-1 md:hidden">{statusAndDocument(report)}</div>
                  </td>
                  <td className={tdClass}>{report.asset?.name || "—"}</td>
                  <td className={clsx(tdClass, hiddenOnPhone, "whitespace-nowrap tabular-nums")}>{number(report.km)}</td>
                  <td className={clsx(tdClass, hiddenOnPhone, "whitespace-nowrap tabular-nums")}>{number(report.consumedLitres, 2)}</td>
                  <td className={clsx(tdClass, "whitespace-nowrap tabular-nums")}>{number(report.amount, 2)}</td>
                  <td className={clsx(tdClass, hiddenOnPhone)}>{statusAndDocument(report)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <TablePagination page={page} limit={data.limit} total={data.total} onPageChange={setPage} />
    </section>
  );
}
